// Saira is bundled rather than loaded from Google Fonts, so a page view sends no
// visitor's IP address to a third party (#4884). Only the weights the tokens use.
import '@fontsource/saira/400.css'
import '@fontsource/saira/500.css'
import '@fontsource/saira/600.css'
import '@fontsource/saira/700.css'
import './assets/main.css'
import { initChunkErrorHandling } from './utils/chunkErrorHandling'
import { shouldDropSentryEvent } from './utils/sentryEventFilter'
import { applyAxiosEventGrouping } from './utils/sentryAxiosGrouping'

// Install chunk error handlers BEFORE any dynamic imports happen
// This catches errors from Vite's preload mechanism that occur before Vue Router can handle them
initChunkErrorHandling()

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'

import App from './App.vue'
import router from './router'
import { setupI18n } from './i18n'
import { useAuthStore } from './stores/auth'
import { getInitialFeedbackOptions } from './composables/useSentryFeedback'
import { config } from './config'

const app = createApp(App)

// Initialize Sentry error tracking (only if DSN is configured via runtime or build-time config)
if (config.sentryDsn) {
  Sentry.init({
    app,
    dsn: config.sentryDsn,
    environment: import.meta.env.MODE,
    release: config.sentryRelease,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        // Privacy: explicitly pin PHI protection for this GGZ EPD rather than
        // relying on SDK-default masking (NEN-7510 8.12). maskAllText/maskAllInputs
        // are SDK defaults, but pinning them guards against a future regression.
        // blockAllMedia is OFF by default — pinning it prevents replay from
        // capturing images/canvas/SVG on dossier pages (the real PHI exposure).
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // Exclude WebSocket and audio-related URLs from network capture
        // to prevent JSON parsing errors when serializing binary audio data
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/api\.scrivio\.nl/,
        ],
        // Don't capture request/response bodies to avoid issues with binary data
        networkCaptureBodies: false,
      }),
      Sentry.feedbackIntegration(getInitialFeedbackOptions()),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Drop events for known-benign errors — see `shouldDropSentryEvent` for the
    // rules and the trade-offs they carry. What survives is regrouped per
    // failed request, because an axios rejection's stack is axios internals
    // only and Sentry would otherwise file them all as one issue
    // (`applyAxiosEventGrouping`, SCRIVIO-FE-2A).
    beforeSend(event, hint) {
      if (shouldDropSentryEvent(hint.originalException)) return null
      return applyAxiosEventGrouping(event, hint.originalException)
    },
  })
}

const pinia = createPinia()
app.use(pinia)
app.use(router)

// Initialize i18n (Dutch loaded synchronously, other locales lazy loaded on demand)
const i18n = setupI18n()
app.use(i18n)

// Initialize auth state listener
const authStore = useAuthStore()
authStore.initAuthListener()

app.mount('#app')
